import { Component, inject, OnInit } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DialogService } from '@ngneat/dialog';

import { isMobileView } from '@core/utils';
import { GlobalSearchEntity } from '@core/entities/global-search.entity';
import { UseGlobalSearch } from '@core/usecases';

import { BadgeComponent, IconComponent } from '../../ui';
import { PageHeaderComponent } from '../../ui/page-header/page-header.component'; // this relative path is important for this page to render correctly
import { DefaultImageDirective } from '@shared/directives';
import { TruncateTextPipe } from '@shared/pipes';
import { CourseDetailsComponent, NewsDetailsComponent, SurveyDetailComponent, TaskDetailsComponent } from '@shared/components';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';

import { EventDetailDialogComponent } from '@web/_shared/dialogs';

interface SearchSection {
  title: string;
  key: string;
  badgeText: string;
  detailType: string;
  isExpanded: boolean;
  dialogComponent?: any;
  dialogConfig?: any;
  navigateUrl?: string;
}

@UntilDestroy()
@Component({
  selector: 'app-global-search',
  imports: [IconComponent, PageHeaderComponent, TranslateModule, DefaultImageDirective, BadgeComponent, NgxSkeletonLoaderModule, TruncateTextPipe],
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
})
export class GlobalSearchComponent implements OnInit {
  searchValue = '';
  searchResults: GlobalSearchEntity;
  isLoading = true;
  searchSections: SearchSection[];
  protected readonly isMobileView = isMobileView();
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _dialogService = inject(DialogService);
  private readonly _bottomSheetService = inject(NgxBottomSheetService);
  private readonly _router = inject(Router);
  private readonly _useGlobalSearch = new UseGlobalSearch();

  constructor() {
    this._initSearchSectionsData();
  }

  ngOnInit() {
    this._activatedRoute.params.subscribe({
      next: (params) => {
        this.searchValue = params['query'];
        this.isLoading = true;
        this._markAllSectionsAsCollapsed();
        this._findSearchQuery();
      },
    });
  }

  toggleSection(section: any) {
    section.isExpanded = !section.isExpanded;
  }

  handleItemClick(result: any, section: any) {
    if (section.detailType === 'dialog') {
      const dialogConfig = section.dialogConfig ? section.dialogConfig(result) : { data: result };
      dialogConfig.showCloseButton = false;
      this._dialogService.open(section.dialogComponent, dialogConfig);
    } else if (section.detailType === 'navigate' && section.navigateUrl) {
      const navigateUrl = section.navigateUrl.replace('{id}', result.id);
      this._router.navigate([navigateUrl]);
    }
  }

  getTitle(result: any, sectionKey: string): string {
    if (sectionKey === 'instructors') {
      return `${result.firstName} ${result.lastName}`;
    }

    if (!result.title || result.title.trim() === '' || result.title.toLowerCase() === 'undefined') {
      return result.name || 'No Title';
    }

    return result.title || result.name;
  }

  getDescription(result: any, sectionKey: string): string {
    if (sectionKey === 'instructors') {
      return result.email;
    }
    return result.description || result.headline;
  }

  private _findSearchQuery() {
    if (!this.searchValue) return;
    this._useGlobalSearch
      .find(this.searchValue)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          if (!response || (response && Object.keys(response).length === 0)) {
            this.searchResults = null;
            this.isLoading = false;
            return;
          }
          this.searchResults = response;
          this.isLoading = false;
        },
      });
  }

  private _markAllSectionsAsCollapsed() {
    this.searchSections.forEach((section) => {
      section.isExpanded = false;
    });
  }

  private _initSearchSectionsData() {
    this.searchSections = [
      {
        title: 'News',
        key: 'news',
        badgeText: 'News',
        detailType: 'dialog',
        isExpanded: true,
        dialogComponent: NewsDetailsComponent,
        dialogConfig: (result: any) => ({
          data: { news: result },
          size: 'md',
          closeButton: false,
          windowClass: 'dialog-overflow-unset dialog-overflow-y-auto',
        }),
      },
      {
        title: 'Events',
        key: 'events',
        badgeText: 'Event',
        detailType: 'dialog',
        isExpanded: true,
        dialogComponent: EventDetailDialogComponent,
        dialogConfig: (result: any) => ({
          data: { event: result, id: result.id },
          size: 'md',
          closeButton: false,
          windowClass: 'dialog-overflow-unset dialog-overflow-y-auto',
        }),
      },
      {
        title: 'Groups',
        key: 'groups',
        badgeText: 'Group',
        detailType: 'navigate',
        navigateUrl: '/groups/{id}/details',
        isExpanded: true,
      },
      {
        title: 'Tasks',
        key: 'tasks',
        badgeText: 'Task',
        detailType: 'dialog',
        isExpanded: true,
        dialogComponent: TaskDetailsComponent,
        dialogConfig: (result: any) => ({
          data: { task: result },
          size: 'md',
          closeButton: false,
          windowClass: 'dialog-overflow-unset dialog-overflow-y-auto',
        }),
      },
      {
        title: 'FAQs',
        key: 'faqs',
        badgeText: 'FAQ',
        detailType: 'navigate',
        navigateUrl: '/club-tools/faq',
        isExpanded: true,
      },
      {
        title: 'Surveys',
        key: 'surveys',
        badgeText: 'Survey',
        detailType: 'dialog',
        isExpanded: true,
        dialogComponent: SurveyDetailComponent,
        dialogConfig: (result: any) => ({
          data: { survey: result },
          size: 'md',
          closeButton: false,
          windowClass: 'dialog-overflow-unset dialog-overflow-y-auto',
        }),
      },
      {
        title: 'Courses',
        key: 'courses',
        badgeText: 'Course',
        detailType: 'dialog',
        isExpanded: true,
        dialogComponent: CourseDetailsComponent,
        dialogConfig: (result: any) => ({
          data: { courseId: result.id },
          width: '70vw',
          closeButton: false,
          windowClass: 'dialog-overflow-unset dialog-overflow-y-auto',
        }),
      },
      {
        title: 'Instructor',
        key: 'instructors',
        badgeText: 'Instructor',
        detailType: 'navigate',
        navigateUrl: '/courses/instructors',
        isExpanded: true,
      },
      {
        title: 'Rooms',
        key: 'rooms',
        badgeText: 'Room',
        detailType: 'navigate',
        navigateUrl: '/courses/rooms',
        isExpanded: true,
      },
    ];
  }
}
